import { useEffect, useMemo, useState } from 'react';
import { ApiError, api, hoursLabel, quarterLabel, slotLabel, type Candidate, type Flag, type Site, type WeekBuilder as Builder, type WeekPreview } from '../lib/api';
import { dayLabel, quarterOfWeek, weekdayShort } from '../lib/dates';
import { navigate } from '../lib/router';

/**
 * Build one week at one hospital (D42, step 2) — the screen the office has
 * never had. The API could do this from D12; nothing called it, so every
 * week the platform holds arrived as a QGenda import.
 *
 * The form is the engine's own input and nothing more: for each position,
 * who is leaving on the Tuesday and who is taking it. Whoever is leaving
 * is read from the previous period rather than asked for, and the default
 * is that nobody moves. Everything is previewed against every rule before
 * it is saved, and saved as a draft — a draft shows nobody anything until
 * it is finalized and published.
 */
export function WeekBuilder({ siteId, periodStart, sites, editedBy }: { siteId: string; periodStart: string; sites: Site[]; editedBy: string }) {
  const [b, setB] = useState<Builder | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [incoming, setIncoming] = useState<Record<string, string | null>>({});
  // Where nobody is handing over, does the physician start Tuesday MORNING, or is the morning left open?
  const [startsMorning, setStartsMorning] = useState<Record<string, boolean>>({});
  /** The input as last saved, so Finalize can never commit a plan different from the one on screen. */
  const [savedInput, setSavedInput] = useState<string | null>(null);
  const [serverFlags, setServerFlags] = useState<Flag[] | null>(null);
  const [nights, setNights] = useState<(string | null)[]>([]);
  const [preview, setPreview] = useState<WeekPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [hardRefusal, setHardRefusal] = useState<string | null>(null);
  const [override, setOverride] = useState('');
  const [toast, setToast] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;
    setB(null); setError(null); setPreview(null); setToast(null); setProblem(null); setHardRefusal(null);
    api
      .weekBuilder(siteId, periodStart)
      .then((d) => {
        if (ignore) return;
        setB(d);
        setStatus(d.status);
        setSavedInput(null);
        setServerFlags(null);
        // With no handoff, the ordinary case is that whoever takes the position starts that morning.
        setStartsMorning(Object.fromEntries(d.slots.map((s) => [s.positionSlot, d.handoff ? false : s.storedOutgoing !== null || !d.hasDraft])));
        if (d.shape === 'cycle') {
          setIncoming({ 'rounder-1': d.cycle?.rounder1PhysicianId ?? null, 'rounder-2': d.cycle?.rounder2PhysicianId ?? null });
        } else {
          // The default is that nobody moves: whoever holds the position keeps it. A stored draft
          // wins over that — including a draft that deliberately leaves a position open.
          setIncoming(Object.fromEntries(d.slots.map((s) => [s.positionSlot, d.hasDraft ? s.storedIncoming : s.previousHolder])));
        }
        setNights(d.nights ?? []);
      })
      .catch((e: Error) => { if (!ignore) setError(e.message); });
    return () => { ignore = true; };
  }, [siteId, periodStart]);

  const input = useMemo(() => {
    if (!b) return null;
    if (b.shape === 'cycle') {
      const one = incoming['rounder-1'];
      const two = incoming['rounder-2'];
      if (!one || !two) return null;
      return { cycleStartTuesday: b.periodStart, rounder1PhysicianId: one, rounder2PhysicianId: two };
    }
    const slots: Record<string, { outgoing: string | null; incoming: string | null }> = {};
    for (const s of b.slots) {
      const taking = incoming[s.positionSlot] ?? null;
      // Whoever is leaving, when somebody is. With no handoff the choice is whether the person
      // taking the position works the Tuesday morning too, or the morning is left open.
      const leaving = b.handoff ? s.previousHolder : startsMorning[s.positionSlot] ? taking : null;
      slots[s.positionSlot] = { outgoing: leaving, incoming: taking };
    }
    return { weekStartTuesday: b.periodStart, slots, ...(b.takesNights ? { nights: padNights(nights) } : {}) };
  }, [b, incoming, nights, startsMorning]);

  // Preview every change against every rule, so nothing is a surprise at finalize.
  useEffect(() => {
    if (!b || b.blocked || !input) { setPreview(null); return; }
    let ignore = false;
    const t = setTimeout(() => {
      const asked = JSON.stringify(input);
      api
        .previewWeek(siteId, periodStart, input)
        .then((p) => { if (!ignore && asked === JSON.stringify(input)) { setPreview(p); setPreviewError(null); } })
        .catch((e: Error) => { if (!ignore) { setPreview(null); setPreviewError(e instanceof ApiError ? e.message : e.message); } });
    }, 250);
    return () => { ignore = true; clearTimeout(t); };
  }, [siteId, periodStart, input, b]);

  /**
   * Planning the week again rewrites its days, so anything somebody changed day by day since
   * it was last planned would go with it. The server refuses rather than allowing that
   * quietly; this asks, names the number, and only then says to go ahead (D51).
   */
  const saveWithConfirmation = async () => {
    try {
      await api.saveDraft(siteId, periodStart, input);
      return;
    } catch (e) {
      const n = e instanceof ApiError && e.body && typeof e.body === 'object' && 'handEdits' in e.body ? Number((e.body as { handEdits: unknown }).handEdits) : 0;
      if (!n) throw e;
      const ok = window.confirm(
        `This week has ${n} change${n === 1 ? '' : 's'} made day by day since it was planned — a swap or a sick day, changed on the week page.\n\n` +
          `Planning it again replaces every day of it, so ${n === 1 ? 'that change goes' : 'those changes go'}. ${n === 1 ? 'It stays' : 'They stay'} in the week's edit history.\n\n` +
          `Plan the week again anyway?`,
      );
      if (!ok) throw new Error('Left as it is — the week still has the changes made day by day.');
      await api.saveDraft(siteId, periodStart, input, { discardEdits: true });
    }
  };

  const act = async (what: string, fn: () => Promise<unknown>, done: string) => {
    setBusy(what); setProblem(null); setHardRefusal(null); setToast(null); setServerFlags(null);
    try {
      await fn();
      setToast(done);
    } catch (e) {
      const body = e instanceof ApiError && e.status === 422 && e.body && typeof e.body === 'object' ? (e.body as { hard?: boolean; message?: string; flags?: Flag[] }) : null;
      if (body?.hard) setHardRefusal(body.message ?? 'Not allowed.');
      else {
        // The server ran the rules again and disagreed with the preview: show ITS findings, and
        // the override box, rather than asking for a reason with nowhere to put it.
        if (body?.flags) setServerFlags(body.flags);
        setProblem(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setBusy(null);
    }
  };

  if (error) return <p className="error">Couldn't open the builder ({error}).</p>;
  if (!b) return <p className="muted">Loading…</p>;

  const site = sites.find((s) => s.id === siteId);
  const flags = serverFlags ?? preview?.flags ?? [];
  const errors = flags.filter((f) => f.severity === 'error');
  const warnings = flags.filter((f) => f.severity === 'warning');
  const hard = errors.filter((f) => f.hard);
  const ready = !!input && !b.blocked;
  const settled = status === 'finalized' || status === 'published';
  // Finalize builds from the SAVED draft, so it must never be reachable while the screen shows something else.
  const unsaved = !!input && savedInput !== null && savedInput !== JSON.stringify(input);
  const needsOverride = errors.length > hard.length;

  return (
    <section className="weekpage builder">
      <div className="crumbs">
        <button className="linklike" type="button" onClick={() => navigate(`/admin/grid?quarter=${quarterOfWeek(periodStart)}`)}>‹ back to the {quarterLabel(quarterOfWeek(periodStart))} grid</button>
      </div>
      <header className="weekhead">
        <h1>
          {site && <span className={`letter site-${siteId}`}>{site.letter}</span>} Build {b.siteName} · {b.shape === 'cycle' ? 'cycle' : 'week'} of {dayLabel(b.periodStart)}
        </h1>
        <div className="chips">
          <span className="pill">{dayLabel(b.periodStart).replace(/^\w+, /, '')} – {dayLabel(b.periodEnd).replace(/^\w+, /, '')}</span>
          {status ? <span className={`pill status-${status}`}>{status}</span> : <span className="pill">not started</span>}
          {b.previousPeriodStart && <span className="pill" title="Where the outgoing physicians come from">follows {dayLabel(b.previousPeriodStart).replace(/^\w+, /, '')}</span>}
        </div>
      </header>

      {b.blocked && <p className="notice">{b.blocked} <button className="linklike" type="button" onClick={() => navigate(`/admin/sites/${siteId}/periods/${periodStart}`)}>Open the week</button></p>}

      {!b.blocked && (
        <>
          <div className="card">
            <h2>{b.shape === 'cycle' ? 'Who works this cycle' : 'Who holds each position'}</h2>
            {b.shape === 'handoff' && (
              <p className="muted small">
                {b.handoff
                  ? `The physician leaving on Tuesday is whoever held the position through ${dayLabel(b.lastPeriodEnd ?? b.periodStart).replace(/^\w+, /, '')} — that isn't a choice, so it's shown rather than asked. Leave a position on the same name and nobody moves.`
                  : b.lastPeriodStart
                    ? `Nobody is handing over: nothing is scheduled here for the ${dayLabel(b.periodStart).replace(/^\w+, /, '')} week's run-up (the last period ended ${dayLabel(b.lastPeriodEnd ?? b.lastPeriodStart).replace(/^\w+, /, '')}). Whoever takes a position starts that Tuesday.`
                    : 'This is the first period this hospital has on the platform, so nobody is handing over. Whoever takes a position starts that Tuesday.'}
              </p>
            )}
            <ul className="buildslots">
              {b.slots.map((s) => (
                <SlotRow
                  key={s.positionSlot}
                  siteId={siteId}
                  date={firstWorkingDay(b)}
                  slot={s.positionSlot}
                  shape={b.shape}
                  handoff={b.handoff}
                  previousHolder={s.previousHolder}
                  lastHeldBy={s.lastHeldBy}
                  lastHeldPeriod={s.lastHeldPeriod}
                  startsMorning={startsMorning[s.positionSlot] ?? true}
                  onStartsMorning={(v) => setStartsMorning((m) => ({ ...m, [s.positionSlot]: v }))}
                  value={incoming[s.positionSlot] ?? null}
                  onChange={(v) => setIncoming((m) => ({ ...m, [s.positionSlot]: v }))}
                />
              ))}
            </ul>
            {b.shape === 'cycle' && incoming['rounder-1'] && incoming['rounder-1'] === incoming['rounder-2'] && (
              <p className="error small">Rounder 1 and Rounder 2 have to be two different physicians.</p>
            )}
          </div>

          {b.takesNights && (
            <div className="card">
              <h2>Nights</h2>
              <p className="muted small">One physician per night, Tuesday to Monday. Asante's nocturnist blocks don't line up with the Tuesday week, so each night is its own choice. Leave a night empty and it shows as an open slot.</p>
              <ul className="buildnights">
                {b.days.map((d, i) => (
                  <NightRow key={d} siteId={siteId} date={d} value={nights[i] ?? null} onChange={(v) => setNights((n) => { const copy = padNights(n); copy[i] = v; return copy; })} />
                ))}
              </ul>
            </div>
          )}

          <div className="card">
            <h2>What this builds</h2>
            {previewError && <p className="error">{previewError}</p>}
            {!preview && !previewError && <p className="muted">{ready ? 'Checking…' : 'Choose who works, and this shows the week it would build.'}</p>}
            {preview && (
              <>
                <p className={hard.length ? 'error' : errors.length ? 'warn' : 'ok'}>
                  {preview.shifts.length} shifts ·{' '}
                  {errors.length === 0 ? 'no errors' : `${errors.length} error${errors.length === 1 ? '' : 's'}`}
                  {warnings.length > 0 && ` · ${warnings.length} warning${warnings.length === 1 ? '' : 's'}`}
                </p>
                {errors.length > 0 && (
                  <ul className="flaglist">
                    {errors.map((f, i) => <li key={`e${i}`} className="err">{f.message}{f.hard && <span className="pill tiny warn" title="Credentialing (D41) — no override exists">cannot be overridden</span>}</li>)}
                  </ul>
                )}
                {warnings.length > 0 && (
                  <details className="flags">
                    <summary>{warnings.length} warning{warnings.length === 1 ? '' : 's'}</summary>
                    <ul className="flaglist">{warnings.map((f, i) => <li key={`w${i}`} className="warn">{f.message}</li>)}</ul>
                  </details>
                )}
                <PreviewDays preview={preview} />
              </>
            )}
          </div>

          <div className="card">
            <h2>Save it</h2>
            <ol className="steps">
              <li className={status ? 'done' : ''}><strong>Draft</strong> — stored, checked, shown to nobody.</li>
              <li className={status === 'finalized' || status === 'published' ? 'done' : ''}><strong>Finalize</strong> — locks the plan and runs every rule.</li>
              <li className={status === 'published' ? 'done' : ''}><strong>Publish</strong> — physicians see it on “My shifts”.</li>
            </ol>
            {hardRefusal && <p className="error"><strong>Not allowed.</strong> {hardRefusal}</p>}
            {problem && <p className="error">{problem}</p>}
            {toast && <p className="ok">{toast}</p>}
            {unsaved && !settled && <p className="notice small">This differs from the saved draft. Finalize builds from what is saved, so save it again first.</p>}
            {needsOverride && !settled && (
              <label className="field">
                <span>Finalizing with {errors.length - hard.length} error{errors.length - hard.length === 1 ? '' : 's'} needs a reason, recorded with the week</span>
                <input value={override} maxLength={1000} placeholder="e.g. John confirmed Dr. Blake by phone" onChange={(e) => setOverride(e.target.value)} />
              </label>
            )}
            {b.handEdits > 0 && (
              <p className="warn small">
                This week has {b.handEdits} change{b.handEdits === 1 ? '' : 's'} made day by day since it was planned — a swap or a sick
                day, entered on <button type="button" className="linklike" onClick={() => navigate(`/admin/sites/${siteId}/periods/${periodStart}`)}>the week page</button>.
                What is above is the PLAN; Finalize commits the week as it stands on that page. Saving the plan again replaces every day of
                it, and will ask first.
              </p>
            )}
            <div className="formactions">
              <button
                type="button"
                className="primary"
                disabled={!ready || busy !== null || settled}
                onClick={() => act('draft', async () => { await saveWithConfirmation(); setStatus('draft'); setSavedInput(JSON.stringify(input)); }, 'Saved as a draft. Nobody sees it yet.')}
              >
                {busy === 'draft' ? 'Saving…' : status ? 'Save the draft again' : 'Save as a draft'}
              </button>
              <button
                type="button"
                disabled={status !== 'draft' || busy !== null || hard.length > 0 || unsaved || (needsOverride && !override.trim())}
                onClick={() => act('finalize', async () => { await api.finalizeWeek(siteId, periodStart, { finalizedBy: editedBy || undefined, ...(override.trim() ? { overrideReason: override.trim() } : {}) }); setStatus('finalized'); }, 'Finalized. Publish it when you are ready.')}
              >
                {busy === 'finalize' ? 'Finalizing…' : unsaved ? 'Save the draft first' : 'Finalize'}
              </button>
              <button
                type="button"
                disabled={status !== 'finalized' || busy !== null}
                onClick={() => act('publish', async () => { await api.publishWeek(siteId, periodStart, { publishedBy: editedBy || undefined }); setStatus('published'); }, 'Published — physicians can see it now.')}
              >
                {busy === 'publish' ? 'Publishing…' : 'Publish'}
              </button>
              {status && <button type="button" className="linklike" onClick={() => navigate(`/admin/sites/${siteId}/periods/${periodStart}`)}>open the week page</button>}
            </div>
            {hard.length > 0 && <p className="muted small">The errors above are credentialing. There is no override for them — credential the physician on the Roster page, or choose somebody else.</p>}
          </div>
        </>
      )}
    </section>
  );
}

function SlotRow({ siteId, date, slot, shape, handoff, previousHolder, lastHeldBy, lastHeldPeriod, startsMorning, onStartsMorning, value, onChange }: { siteId: string; date: string; slot: string; shape: 'handoff' | 'cycle'; handoff: boolean; previousHolder: string | null; lastHeldBy: string | null; lastHeldPeriod: string | null; startsMorning: boolean; onStartsMorning: (v: boolean) => void; value: string | null; onChange: (v: string | null) => void }) {
  const cands = useCandidates(siteId, slot, date);
  const label = slotLabel(slot);
  return (
    <li className="buildslot">
      <div className="slotname">
        {label}
        {shape === 'handoff' && (
          <div className="muted small">
            {handoff
              ? previousHolder
                ? `${previousHolder} hands it over`
                : 'nobody held it last period'
              : lastHeldBy
                ? `last held by ${lastHeldBy}, week of ${dayLabel(lastHeldPeriod ?? '').replace(/^\w+, /, '')}`
                : 'never held here'}
          </div>
        )}
      </div>
      <div className="pickwrap">
        <Picker label={`${label} — who takes it`} cands={cands} value={value} onChange={onChange} allowOpen={shape === 'handoff'} />
        {shape === 'handoff' && !handoff && value && (
          <label className="starts">
            <input type="checkbox" checked={startsMorning} onChange={(e) => onStartsMorning(e.target.checked)} /> works the Tuesday morning too
          </label>
        )}
      </div>
    </li>
  );
}

function NightRow({ siteId, date, value, onChange }: { siteId: string; date: string; value: string | null; onChange: (v: string | null) => void }) {
  const cands = useCandidates(siteId, 'night', date);
  return (
    <li className="buildslot">
      <div className="slotname"><span className="wd">{weekdayShort(date)}</span> {dayLabel(date).replace(/^\w+, /, '')}</div>
      <Picker label={`${dayLabel(date).replace(/^\w+, /, '')} night`} cands={cands} value={value} onChange={onChange} allowOpen />
    </li>
  );
}

function Picker({ label, cands, value, onChange, allowOpen }: { label: string; cands: Candidate[] | null; value: string | null; onChange: (v: string | null) => void; allowOpen: boolean }) {
  if (cands === null) return <span className="muted small">checking who's eligible…</span>;
  const chosen = cands.find((c) => c.abbreviation === value);
  return (
    <div className="pick">
      <select aria-label={label} value={value ?? ''} onChange={(e) => onChange(e.target.value || null)}>
        {allowOpen && <option value="">— leave open —</option>}
        {!allowOpen && <option value="">— choose —</option>}
        {cands.map((c) => (
          <option key={c.abbreviation} value={c.abbreviation} disabled={c.blockers.length > 0}>
            {c.lastName}, {c.firstName}{c.blockers.length ? ` — ${c.blockers[0]}` : c.notes.length ? ` (${c.notes[0]})` : ''}
          </option>
        ))}
        {/* Somebody already stored who is no longer eligible must stay visible rather than silently vanish. */}
        {value && !chosen && <option value={value}>{value} — no longer eligible</option>}
      </select>
      {chosen?.notes.map((n, i) => <span key={i} className="why warn">{n}</span>)}
      {value && !chosen && <span className="why err">not eligible for this position any more</span>}
    </div>
  );
}

function PreviewDays({ preview }: { preview: WeekPreview }) {
  const byDay = useMemo(() => {
    const m = new Map<string, WeekPreview['shifts']>();
    for (const s of preview.shifts) m.set(s.date, [...(m.get(s.date) ?? []), s]);
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [preview]);
  return (
    <ol className="days compact">
      {byDay.map(([date, rows]) => (
        <li key={date} className="day">
          <div className="dayhead"><span className="weekday">{weekdayShort(date)}</span> <span className="date">{dayLabel(date).replace(/^\w+, /, '')}</span></div>
          <table className="shifts-table">
            <tbody>
              {rows.map((s, i) => (
                <tr key={i} className={s.physicianId ? '' : 'open-row'}>
                  <td className="slot">{slotLabel(s.positionSlot)}</td>
                  <td className="hours">{s.start && s.end ? hoursLabel(s.start, s.end, s.crossesMidnight) : 'on call'}</td>
                  <td className="who">{s.physicianId ?? <em>open</em>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </li>
      ))}
    </ol>
  );
}

function useCandidates(siteId: string, slot: string, date: string): Candidate[] | null {
  const [cands, setCands] = useState<Candidate[] | null>(null);
  useEffect(() => {
    let ignore = false;
    setCands(null);
    api.candidates(siteId, slot, date).then((c) => { if (!ignore) setCands(c); }).catch(() => { if (!ignore) setCands([]); });
    return () => { ignore = true; };
  }, [siteId, slot, date]);
  return cands;
}

/** The engine wants exactly seven nights, Tuesday to Monday. */
function padNights(n: (string | null)[]): (string | null)[] {
  const out = [...n];
  while (out.length < 7) out.push(null);
  return out.slice(0, 7);
}

/** Who is on the position the day after the handoff — the right day to ask "who could fill this?" about. */
function firstWorkingDay(b: Builder): string {
  return b.days[1] ?? b.days[0] ?? b.periodStart;
}
